import { useState } from 'react'

import _ from 'lodash'
import qs from 'qs'
import { useParams } from 'react-router-dom'

import {
  LoadingContainer,
  Modal,
  Select,
  NumericInput,
  Input,
  ModelList,
  DateInput,
  Panels,
  Pagination,
  Bar,
} from 'peach/components'
import { PaymentInstrumentSelect } from 'peach/scopes/person'

import { Page } from 'core/components'
import { useQueryString } from 'core/hooks'
import {
  useLoanTransactionsGetAll,
  useInternalTransactionsCreate,
  useInternalBorrowerPaymentInstrumentCreate,
  PickRequired,
} from 'core/types'
import { LoanTransactionsGetAllVariables } from 'core/types/operations'

const LoanTransactions = () => {
  const { companyId, personId, loanId } = useParams<{
    companyId?: string
    personId?: string
    loanId?: string
  }>()

  const [page, setPage] = useState(1)

  const [query, setQuery] = useQueryString<
    PickRequired<
      NonNullable<LoanTransactionsGetAllVariables['query']>,
      'limit' | 'startingAfter' | 'endingBefore'
    >
  >({
    limit: 50,
    startingAfter: '',
    endingBefore: '',
  })

  const {
    data: transactions,
    pagination: { total: count, nextUrl, previousUrl } = {},
    isLoading,
    error,
    refetch,
  } = useLoanTransactionsGetAll({
    companyId,
    personId,
    loanId,
    query,
  })

  const nextTransaction = qs.parse(nextUrl ?? '')?.startingAfter
  const previousTransaction = qs.parse(previousUrl ?? '')?.endingBefore

  const { mutateAsync: createTransaction } = useInternalTransactionsCreate({
    companyId,
    personId,
    loanId,
  })

  const { mutateAsync: createPaymentInstrument } =
    useInternalBorrowerPaymentInstrumentCreate({
      companyId,
      personId,
    })

  const sendCreateTransaction = async (transaction) => {
    await createTransaction(transaction)
  }

  const types = ['oneTime', 'service', 'downPayment']

  const newTransaction = {
    paymentInstrumentId: null,
    amount: 100,
    type: 'oneTime',
  }

  const sendCreateExternalCheck = async (options) => {
    const {
      // instrument props
      accountNumber,
      routingNumber,
      checkNumber,
      accountHolderType,
      accountHolderName,
      // transaction props
      effectiveDate,
      amount,
    } = options

    const resp = await createPaymentInstrument({
      isExternal: true,
      instrumentType: 'check',
      accountNumber,
      routingNumber,
      checkNumber,
      accountHolderType,
      accountHolderName,
    })

    const instrument = _.isArray(resp.data) ? _.first(resp.data) : resp.data

    const paymentInstrumentId = instrument.id

    await createTransaction({
      isExternal: true,
      type: 'oneTime',
      paymentInstrumentId,
      amount,
      effectiveDate,
    })

    refetch()
  }

  const columns = [
    {
      label: 'Info',
      renderJson: (txn) => ({
        effectiveDate: txn.timestamps.effectiveDate,
        createdBy: txn.createdBy?.name,
      }),
    },
  ]

  return (
    <Panels
      header={
        <Bar
          left={
            <Pagination
              limit={query.limit ?? 0}
              onChange={(direction) => {
                if (direction === 'previous') {
                  setQuery({
                    ...query,
                    startingAfter: '',
                    endingBefore: page > 2 ? previousTransaction : '',
                  })
                  setPage(page - 1)
                } else {
                  setQuery({
                    ...query,
                    startingAfter: nextTransaction,
                    endingBefore: '',
                  })
                  setPage(page + 1)
                }
              }}
              page={page}
              total={count}
              variant='compact'
            />
          }
          title='Transactions'
        />
      }
    >
      <LoadingContainer error={error} loading={isLoading}>
        <Modal
          initialValue={newTransaction}
          onSubmit={sendCreateExternalCheck}
          title='Create External Check Transaction'
        >
          <Input formKey='accountNumber' />
          <Input formKey='routingNumber' />
          <Input formKey='checkNumber' />
          <Select
            formKey='accountHolderType'
            options={['business', 'personal']}
          />
          <Input formKey='accountHolderName' />
          <NumericInput formKey='amount' />
          <DateInput formKey='effectiveDate' />
        </Modal>
        <Modal
          initialValue={newTransaction}
          onSubmit={sendCreateTransaction}
          title='Create Transaction'
        >
          <Select formKey='type' options={types} />
          <PaymentInstrumentSelect formKey='paymentInstrumentId' />
          <NumericInput formKey='amount' />
          <Input formKey='scheduledDate' />
        </Modal>
        <Page variant='full'>
          <ModelList
            columns={columns}
            json={transactions}
            title='Transactions'
            withDetails
            withRelated
            withTimestamps
          />
        </Page>
      </LoadingContainer>
    </Panels>
  )
}

export default LoanTransactions
